import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Select,
  Space,
  Typography,
  Table,
  DatePicker,
  Button,
  Statistic,
  Tag,
  Modal,
  Descriptions,
  message,
  Tooltip,
} from "antd";
import { EnvironmentOutlined, FileTextOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { getRouteVisitReportApi } from "../../../apiservice/hrpayroll";
import { formatToCompanyTimezone } from "../../../lib/functions";
import { convertAdhocToRoutePoint } from "../workRoutesThunk";

const RouteHistory = ({ center, zoom, isEmployee = false, employeeId = null }) => {
  const { routeAssignments, routeAreas } = useSelector(
    (state) => state.workRoutes,
  );

  const [selectedEmployee, setSelectedEmployee] = useState(isEmployee ? employeeId : null);
  const [visitType, setVisitType] = useState("all");
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, "day"),
    dayjs(),
  ]);
  const [visitLogs, setVisitLogs] = useState([]);
  const [summary, setSummary] = useState({});
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedMapVisit, setSelectedMapVisit] = useState(null);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [selectedAdhocVisit, setSelectedAdhocVisit] = useState(null);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [routeMapModalOpen, setRouteMapModalOpen] = useState(false);
  const [selectedDayVisits, setSelectedDayVisits] = useState([]);
  const [selectedRouteDate, setSelectedRouteDate] = useState(null);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [executionNote, setExecutionNote] = useState(null);
  const [dailySummary, setDailySummary] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const dispatch = useDispatch();

  const fetchReport = async ( employeeOverride = null ) => {
    try {
      const reportEmployeeId =
      typeof employeeOverride === "string" || typeof employeeOverride === "number"
        ? employeeOverride
        : selectedEmployee;

      if (!reportEmployeeId) {
        return;
      }

      const res = await getRouteVisitReportApi({
        employee_id: reportEmployeeId,
        from_date: dateRange[0].format("YYYY-MM-DD"),
        to_date: dateRange[1].format("YYYY-MM-DD"),
        visit_type: visitType === "all" ? null : visitType,
      });
      setSummary(res.data.data?.summary || {});
      setDailySummary(res.data.data?.daily_summary || []);
      setVisitLogs(res.data.data?.visits || []);
      setExecutionLogs(res.data.data?.execution_logs || []);
      setPagination((prev) => ({ ...prev, current: 1 }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleConvertToRoutePoint = async () => {
    try {
      if (!selectedAreaId) {
        message.error("Please select area");
        return;
      }
      await dispatch(
        convertAdhocToRoutePoint({
          adhoc_visit_id: selectedAdhocVisit.id,
          area_id: selectedAreaId,
        }),
      ).unwrap();
      message.success("Converted successfully");
      setConvertModalOpen(false);
      setSelectedAreaId(null);
      setSelectedAdhocVisit(null);
      fetchReport();
    } catch (err) {
      message.error(err?.message || "Conversion failed");
    }
  };

  const employeeOptions = [
    ...new Map(
      routeAssignments.map((assignment) => [
        assignment.employee_id,
        { label: assignment.employee_name, value: assignment.employee_id },
      ]),
    ).values(),
  ];

  const executionMap = executionLogs.reduce((acc, item) => {
    acc[
      dayjs(item.execution_date).format("YYYY-MM-DD")
    ] = item;

    return acc;
  }, {});

  useEffect(() => {
    if (isEmployee && employeeId) {
      setSelectedEmployee(employeeId);
    }
  }, [isEmployee, employeeId]);

  useEffect(() => {
    if (!isEmployee || !employeeId) {
      return;
    }

    fetchReport(employeeId);
  }, [isEmployee, employeeId]);

  const reasonMap = {
    sick_leave: "Sick Leave",
    casual_leave: "Casual Leave",
    annual_leave: "Annual Leave",
    personal_emergency:
      "Personal Emergency",
    vehicle_breakdown:
      "Vehicle Breakdown",
    emergency_duty:
      "Emergency Duty",
    other: "Other",
  };

  // const dailySummary = Object.values(
  //   visitLogs.reduce((acc, visit) => {
  //     const date = dayjs(visit.checkin_time).format("YYYY-MM-DD");
  //     if (!acc[date]) {
  //       const execution = executionMap[date];
  //       acc[date] = {
  //         date,
  //         visits: [],
  //         route_visits: 0,
  //         adhoc_visits: 0,
  //         total_duration: 0,
  //         execution_status: execution?.execution_status,
  //         execution_reason: execution?.execution_reason,
  //         execution_notes: execution?.notes,
  //         schedule_type: visit.schedule_type,
  //         schedule_days: visit.schedule_days,
  //         assignment_date: visit.assignment_date,
  //         schedule_end_date: visit.schedule_end_date,
  //         is_temporary: visit.is_temporary,
  //       };
  //     }
  //     acc[date].visits.push(visit);
  //     if (visit.visit_type === "route_point") {
  //       acc[date].route_visits++;
  //     } else {
  //       acc[date].adhoc_visits++;
  //     }
  //     acc[date].total_duration += Number(visit.duration_minutes || 0);
  //     return acc;
  //   }, {}),
  // );

  // executionLogs.forEach((log) => {
  //   const logDate = dayjs(log.execution_date).format("YYYY-MM-DD");

  //   const exists = dailySummary.find((d) => d.date === logDate);

  //   if (!exists) {
  //     dailySummary.push({
  //       date: dayjs(log.execution_date).format("YYYY-MM-DD"),
  //       visits: [],
  //       route_visits: 0,
  //       adhoc_visits: 0,
  //       total_duration: 0,
  //       execution_status: log.execution_status,
  //       execution_reason: log.execution_reason,
  //       execution_notes: log.notes,
  //       schedule_type: log.schedule_type,
  //       schedule_days: log.schedule_days,
  //       schedule_week: log?.schedule_week,
  //       rotation_cycle: log?.rotation_cycle,
  //       rotation_week: log?.rotation_week,
  //       assignment_date: log.assignment_date,
  //       schedule_end_date: log.schedule_end_date,
  //       is_temporary: log.is_temporary,
  //     });
  //   }
  // });

  const mergedDailySummary = useMemo(() => {
  const result = [...dailySummary];

  executionLogs.forEach((log) => {
      const logDate = dayjs(log.execution_date).format("YYYY-MM-DD");

      const exists = result.some((d) => d.date === logDate);

      if (!exists) {
        result.push({
          date: logDate,
          visits: [],
          route_visits: 0,
          adhoc_visits: 0,
          visit_duration: 0,
          total_route_duration: 0,
          travel_duration: 0,
          execution_status: log.execution_status,
          execution_reason: log.execution_reason,
          execution_notes: log.notes,
          schedule_type: log.schedule_type,
          schedule_days: log.schedule_days,
          schedule_week: log.schedule_week,
          rotation_cycle: log.rotation_cycle,
          rotation_week: log.rotation_week,
          assignment_date: log.assignment_date,
          schedule_end_date: log.schedule_end_date,
          is_temporary: log.is_temporary,
        });
      }
    });

    return result.sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  }, [dailySummary, executionLogs]);

  // dailySummary?.sort((a, b) => new Date(b.date) - new Date(a.date));

  const getDateLabel = (date) => {
    const today = dayjs();
    const target = dayjs(date);
    if (target.format("YYYY-MM-DD") === today.format("YYYY-MM-DD"))
      return "Today";
    if (
      target.format("YYYY-MM-DD") ===
      today.subtract(1, "day").format("YYYY-MM-DD")
    )
      return "Yesterday";
    return target.format("DD-MM-YYYY");
  };

  const groupedVisitLogs = [];
  let lastDate = null;
  visitLogs.forEach((visit) => {
    const visitDate = dayjs(visit.checkin_time).format("YYYY-MM-DD");
    if (visitDate !== lastDate) {
      groupedVisitLogs.push({
        id: `header-${visitDate}`,
        isDateHeader: true,
        dateLabel: getDateLabel(visitDate),
      });
      lastDate = visitDate;
    }
    groupedVisitLogs.push(visit);
  });

  return (
    <div>
      {/* Dynamic responsive mobile table layout overrides */}
      <style>{`
        /* Desktop & Tablet View Fixes (768px and up) */
        @media (min-width: 768px) {
          .responsive-table {
            width: 100% !important;
          }
          .responsive-table .ant-table {
            table-layout: auto !important;
          }
        }

        /* Mobile View Card Layout Transformation (767px and below) */
        @media (max-width: 767px) {
          .responsive-table .ant-table-thead { 
            display: none; 
          }
          .responsive-table .ant-table-tbody > tr { 
            display: block; 
            border: 1px solid #f0f0f0; 
            border-radius: 8px; 
            margin-bottom: 12px; 
            padding: 8px; 
            background: #fff; 
          }
          .responsive-table .ant-table-tbody > tr.route-date-header-row { 
            background: #fafafa; 
            border: none; 
            margin-bottom: 4px; 
            padding: 4px 8px; 
          }
          .responsive-table .ant-table-tbody > tr > td { 
            display: flex; 
            justify-content: space-between; 
            align-items: center; 
            border-bottom: none !important; 
            padding: 6px 8px !important; 
            text-align: right; 
          }
          .responsive-table .ant-table-tbody > tr > td::before { 
            content: attr(data-label); 
            font-weight: 600; 
            text-align: left; 
            padding-right: 16px; 
            font-size: 13px; 
            color: rgba(0, 0, 0, 0.65); 
          }
          .responsive-table .ant-table-tbody > tr.route-date-header-row > td { 
            display: block; 
            text-align: left; 
            padding: 4px 0 !important; 
          }
          .responsive-table .ant-table-tbody > tr.route-date-header-row > td::before { 
            content: none; 
          }
        }
      `}</style>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={8} lg={6}>
          <Card title="Visit Summary">
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              {!isEmployee && (
                <Select
                  placeholder="Select Employee"
                  value={selectedEmployee}
                  onChange={setSelectedEmployee}
                  options={employeeOptions}
                  style={{ width: "100%" }}
                />
              )}
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                value={dateRange}
                onChange={setDateRange}
              />
              <Select
                value={visitType}
                onChange={setVisitType}
                options={[
                  { label: "All Visits", value: "all" },
                  { label: "Route Visits", value: "route_point" },
                  { label: "Adhoc Visits", value: "adhoc" },
                ]}
                style={{ width: "100%" }}
              />
              <Button
                type="primary"
                block
                disabled={!selectedEmployee}
                onClick={() => fetchReport()}
              >
                Generate Report
              </Button>
              <Card
                size="small"
                style={{ background: "#fafafa", border: "1px solid #f0f0f0" }}
              >
                <Row gutter={[8, 8]}>
                  <Col xs={12} sm={6} md={24}>
                    <Statistic
                      title="Total Visits"
                      value={summary.total_visits || 0}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={24}>
                    <Statistic
                      title="Route Visits"
                      value={summary.route_visits || 0}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={24}>
                    <Statistic
                      title="Adhoc Visits"
                      value={summary.adhoc_visits || 0}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={24}>
                    <Statistic
                      title="Duration (mins)"
                      value={summary.total_route_duration || 0}
                    />
                  </Col>
                </Row>
              </Card>
            </Space>
          </Card>
        </Col>

        <Col xs={24} md={16} lg={18}>
          <Card title="Daily Route Summary" style={{ marginBottom: 16 }}>
            <Table
              className="responsive-table"
              rowClassName={(record) => {
                switch (record.execution_status) {
                  case "completed":
                    return "route-completed-row";

                  case "partially_completed":
                    return "route-partial-row";

                  case "not_executed":
                    return "route-not-executed-row";

                  default:
                    return "";
                }
              }}
              rowKey="date"
              pagination={false}
              scroll={{ x: "max-content" }}
              dataSource={mergedDailySummary}
              columns={[
                {
                  title: "#",
                  key: "serialNumber",
                  width: 50,
                  align: "center",
                  render: (_, __, index) => index + 1,
                  onCell: () => ({ "data-label": "#" }),
                },
                {
                  title: "Date",
                  dataIndex: "date",
                  onCell: () => ({ "data-label": "Date" }),
                },
                {
                  title: "Assignment",
                  key: "assignment",
                  render: (_, record) => {
                    if (!record.schedule_type) return "-";
                    const tagColor = record.schedule_type === "once" ? "green" : record.is_temporary ? "orange" : "blue";
                    const tagText =
                      record.schedule_type === "once"
                        ? "One Time"
                        : record.schedule_type === "weekly"
                          ? "Weekly"
                          : record.schedule_type
                            ? record.schedule_type.charAt(0).toUpperCase() +
                              record.schedule_type.slice(1)
                            : "-";
                    // const days = record.schedule_days?.length ? record.schedule_days.map((d) => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(", ") : "-";
                    const dayNames = [
                        "Sunday",
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                      ];

                      const dayNameMap = {
                        sunday: "Sunday",
                        monday: "Monday",
                        tuesday: "Tuesday",
                        wednesday: "Wednesday",
                        thursday: "Thursday",
                        friday: "Friday",
                        saturday: "Saturday",
                      };

                      const days =
                        Array.isArray(record.schedule_days) &&
                        record.schedule_days.length
                          ? record.schedule_days
                              .map((day) => {
                                // Numeric format: 0-6
                                if (typeof day === "number" || !isNaN(Number(day))) {
                                  const dayIndex = Number(day);
                                  return dayNames[dayIndex] || String(day);
                                }

                                // Legacy string format: "monday", "tuesday", etc.
                                const normalized = String(day).toLowerCase().trim();

                                return dayNameMap[normalized] || String(day);
                              })
                              .join(", ")
                          : "-";
                    
                    return (
                      <Tooltip
                        title={
                          <div>
                            <div>
                              <strong>Schedule Type:</strong> {tagText}
                            </div>
                            {["weekly", "biweekly", "custom"].includes(record.schedule_type) && (
                              <div>
                                <strong>Days:</strong> {days}
                              </div>
                            )}
                            {record.schedule_type === "biweekly" && (
                              <div>
                                <strong>Rotation Week:</strong>{" "}
                                {record.schedule_week || "-"}
                              </div>
                            )}

                            {record.schedule_type === "custom" && (
                              <>
                                <div>
                                  <strong>Rotation Cycle:</strong>{" "}
                                  {record.rotation_cycle
                                    ? `${record.rotation_cycle} weeks`
                                    : "-"}
                                </div>

                                <div>
                                  <strong>Rotation Week:</strong>{" "}
                                  {record.rotation_week
                                    ? `Week ${record.rotation_week}`
                                    : "-"}
                                </div>
                              </>
                            )}
                            <div>
                              <strong>Start:</strong>{" "}
                              {dayjs(record.assignment_date).format(
                                "DD-MMM-YYYY",
                              )}
                            </div>
                            {record.schedule_end_date && (
                              <div>
                                <strong>End:</strong>{" "}
                                {dayjs(record.schedule_end_date).format(
                                  "DD-MMM-YYYY",
                                )}
                              </div>
                            )}
                          </div>
                        }
                        trigger={["hover", "click"]}
                      >
                        {/* Style wrapper creates an explicit hint on hover while preserving mobile layout scaling */}
                        <span
                          style={{
                            cursor: "help",
                            borderBottom: "1px dashed #d9d9d9",
                            paddingBottom: "2px",
                          }}
                        >
                          <Tag color={tagColor} style={{ marginRight: 0 }}>
                            {tagText} 
                          <InfoCircleOutlined
                            style={{
                              marginLeft: "4px",
                              color: "#0e3e8b",
                              cursor: "pointer",
                            }}
                            />
                            </Tag>
                        </span>
                      </Tooltip>
                    );
                  },
                  onCell: () => ({ "data-label": "Assignment" }),
                },
                {
                  title: "Route Visits",
                  dataIndex: "route_visits",
                  onCell: () => ({ "data-label": "Route Visits" }),
                },
                {
                  title: "Adhoc Visits",
                  dataIndex: "adhoc_visits",
                  onCell: () => ({ "data-label": "Adhoc Visits" }),
                },
                {
                  title: "Visit Time",
                  dataIndex: "visit_duration",
                  render: (value) => `${value || 0} mins`,
                  onCell: () => ({
                    "data-label": "Visit Time",
                  }),
                },

                {
                  title: "Total Route Time",
                  dataIndex: "total_route_duration",
                  render: (value) => `${value || 0} mins`,
                  onCell: () => ({
                    "data-label": "Total Route Time",
                  }),
                },

                {
                  title: "Travel Time",
                  dataIndex: "travel_duration",
                  render: (value) => `${value || 0} mins`,
                  onCell: () => ({
                    "data-label": "Travel Time",
                  }),
                },
                {
                  title: "Status",
                  dataIndex: "execution_status",
                  render: (value) => {
                    if (!value) return "-";

                    switch (value) {
                      case "completed":
                        return <Tag color="green">Completed</Tag>;

                      case "partially_completed":
                        return <Tag color="orange">Partially Completed</Tag>;

                      case "not_executed":
                        return <Tag color="red">Not Executed</Tag>;

                      default:
                        return "-";
                    }
                  },
                },
                {
                  title: "Reason",
                  render: (_, record) => (
                    <div>
                      <div>
                        {reasonMap[record.execution_reason] ||
                          record.execution_reason ||
                          "-"}
                      </div>

                      {record.execution_notes && (
                        <Button
                          size="small"
                          icon={<FileTextOutlined />}
                          onClick={() => {
                            setExecutionNote({
                              reason: record.execution_reason,
                              notes: record.execution_notes,
                            });
                            setSelectedVisit(null);
                            setNotesModalOpen(true);
                          }}
                        >
                          Note
                        </Button>
                      )}
                    </div>
                  ),
                },
                {
                  title: "Route Map",
                  render: (_, record) => {
                    if (
                      record.execution_status === "not_executed" ||
                      !record.visits?.length
                    ) {
                      return <Tag color="red">No Route</Tag>;
                    }
                    return (
                      <Button
                        icon={<EnvironmentOutlined />}
                        onClick={() => {
                          setSelectedDayVisits(
                            [...record.visits].sort(
                              (a, b) =>
                                new Date(a.checkin_time) -
                                new Date(b.checkin_time),
                            ),
                          );
                          setSelectedRouteDate(record.date);
                          setRouteMapModalOpen(true);
                        }}
                      >
                        View Route
                      </Button>
                    );
                  },
                  onCell: () => ({ "data-label": "Route Map" }),
                },
              ]}
            />
          </Card>

          <Card title="Visit History">
            <Table
              className="responsive-table"
              rowKey="id"
              scroll={{ x: "max-content" }}
              dataSource={groupedVisitLogs}
              rowClassName={(record) =>
                record.isDateHeader ? "route-date-header-row" : ""
              }
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ["10", "20", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
                onChange: (page, pageSize) =>
                  setPagination({ current: page, pageSize }),
                onShowSizeChange: (current, size) =>
                  setPagination({ current: 1, pageSize: size }),
              }}
              columns={[
                {
                  title: "#",
                  key: "serialNumber",
                  width: 65,
                  align: "center",
                  render: (_, record, index) => {
                    if (record.isDateHeader) {
                      return {
                        children: (
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: 16,
                              color: "#1890ff",
                            }}
                          >
                            {record.dateLabel}
                          </div>
                        ),
                        props: { colSpan: 10 },
                      };
                    }
                    return (
                      (pagination.current - 1) * pagination.pageSize + index + 1
                    );
                  },
                  onCell: (record) =>
                    record.isDateHeader ? {} : { "data-label": "#" },
                },
                {
                  title: "Type",
                  dataIndex: "visit_type",
                  render: (value, record) => {
                    if (record.isDateHeader) return { props: { colSpan: 0 } };
                    return value === "adhoc" ? (
                      <Tag color="orange">Adhoc</Tag>
                    ) : (
                      <Tag color="blue">Route</Tag>
                    );
                  },
                  onCell: (record) =>
                    record.isDateHeader ? {} : { "data-label": "Type" },
                },
                {
                  title: "Visit",
                  dataIndex: "visit_name",
                  render: (value, record) =>
                    record.isDateHeader ? { props: { colSpan: 0 } } : value,
                  onCell: (record) =>
                    record.isDateHeader ? {} : { "data-label": "Visit" },
                },
                {
                  title: "Route",
                  dataIndex: "route_name",
                  render: (value, record) =>
                    record.isDateHeader
                      ? { props: { colSpan: 0 } }
                      : value || "-",
                  onCell: (record) =>
                    record.isDateHeader ? {} : { "data-label": "Route" },
                },
                {
                  title: "Check In",
                  dataIndex: "checkin_time",
                  render: (v, record) =>
                    record.isDateHeader
                      ? { props: { colSpan: 0 } }
                      : formatToCompanyTimezone(v),
                  onCell: (record) =>
                    record.isDateHeader ? {} : { "data-label": "Check In" },
                },
                {
                  title: "Check Out",
                  dataIndex: "checkout_time",
                  render: (v, record) =>
                    record.isDateHeader
                      ? { props: { colSpan: 0 } }
                      : formatToCompanyTimezone(v),
                  onCell: (record) =>
                    record.isDateHeader ? {} : { "data-label": "Check Out" },
                },
                {
                  title: "Duration",
                  dataIndex: "duration_minutes",
                  render: (v, record) =>
                    record.isDateHeader
                      ? { props: { colSpan: 0 } }
                      : `${v || 0} mins`,
                  onCell: (record) =>
                    record.isDateHeader ? {} : { "data-label": "Duration" },
                },
                {
                  title: "Notes",
                  key: "notes",
                  render: (_, record) => {
                    if (record.isDateHeader) return { props: { colSpan: 0 } };
                    return (
                      <Button
                        size="small"
                        icon={<FileTextOutlined />}
                        onClick={() => {
                          setSelectedVisit(record);
                          setNotesModalOpen(true);
                        }}
                      >
                        View
                      </Button>
                    );
                  },
                  onCell: (record) =>
                    record.isDateHeader ? {} : { "data-label": "Notes" },
                },
                {
                  title: "Map",
                  key: "map",
                  render: (_, record) => {
                    if (record.isDateHeader) return { props: { colSpan: 0 } };
                    return (
                      <Button
                        size="small"
                        icon={<EnvironmentOutlined />}
                        onClick={() => {
                          setSelectedMapVisit(record);
                          setMapModalOpen(true);
                        }}
                      >
                        View
                      </Button>
                    );
                  },
                  onCell: (record) =>
                    record.isDateHeader ? {} : { "data-label": "Map" },
                },
                {
                  title: "Actions",
                  key: "actions",
                  render: (_, record) => {

                    if (record.isDateHeader) return { props: { colSpan: 0 } };

                    if (record.visit_type !== "adhoc") return "-";

                    if (record.converted_to_route_point) {
                      return <Tag color="green">Converted</Tag>;
                    }

                    if (isEmployee) {
                      return "-";
                    }

                    return (
                      <Tooltip title="Convert this adhoc visit point into a reusable Route Point">
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => {
                            setSelectedAdhocVisit(record);
                            setConvertModalOpen(true);
                          }}
                        >
                          Convert
                        </Button>
                      </Tooltip>
                    );
                  },
                  onCell: (record) =>
                    record.isDateHeader ? {} : { "data-label": "Actions" },
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {/* Modals with responsive width fixes */}
      <Modal
        open={notesModalOpen}
        onCancel={() => {
          setNotesModalOpen(false);
          setSelectedVisit(null);
          setExecutionNote(null);
        }}
        footer={null}
        width={700}
        title="Visit Notes"
      >
        {selectedVisit ? (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Visit">
              {selectedVisit.visit_name}
            </Descriptions.Item>

            <Descriptions.Item label="Type">
              {selectedVisit.visit_type}
            </Descriptions.Item>

            <Descriptions.Item label="Check In Notes">
              {selectedVisit.checkin_notes || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Check Out Notes">
              {selectedVisit.checkout_notes || "-"}
            </Descriptions.Item>
          </Descriptions>
        ) : executionNote ? (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Reason">
              {reasonMap[executionNote.reason] || executionNote.reason}
            </Descriptions.Item>

            <Descriptions.Item label="Notes">
              {executionNote.notes || "-"}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Modal>

      <Modal
        open={mapModalOpen}
        onCancel={() => setMapModalOpen(false)}
        footer={null}
        width={900}
        title="Visit Location"
        style={{ top: 20 }}
      >
        {selectedMapVisit && (
          <>
            <div style={{ marginBottom: 6 }}>
              <strong>Visit:</strong> {selectedMapVisit.visit_name}
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong>Route:</strong> {selectedMapVisit.route_name || "-"}
            </div>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "400px" }}
              center={{
                lat: Number(selectedMapVisit.latitude),
                lng: Number(selectedMapVisit.longitude),
              }}
              zoom={16}
            >
              <Marker
                position={{
                  lat: Number(selectedMapVisit.latitude),
                  lng: Number(selectedMapVisit.longitude),
                }}
              />
            </GoogleMap>
          </>
        )}
      </Modal>

      <Modal
        open={convertModalOpen}
        title="Convert To Route Point"
        onCancel={() => {
          setConvertModalOpen(false);
          setSelectedAreaId(null);
          setSelectedAdhocVisit(null);
        }}
        onOk={handleConvertToRoutePoint}
      >
        {selectedAdhocVisit && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <strong>Visit:</strong> {selectedAdhocVisit.visit_name}
            </div>
            <div>
              <strong>Latitude:</strong> {selectedAdhocVisit.latitude}
            </div>
            <div>
              <strong>Longitude:</strong> {selectedAdhocVisit.longitude}
            </div>
            <Select
              placeholder="Select Area"
              value={selectedAreaId}
              onChange={setSelectedAreaId}
              options={routeAreas.map((area) => ({
                label: area.name,
                value: area.id,
              }))}
              style={{ width: "100%" }}
            />
          </Space>
        )}
      </Modal>

      <Modal
        open={routeMapModalOpen}
        footer={null}
        width={1100}
        title={`Route Journey ${selectedRouteDate || ""}`}
        onCancel={() => setRouteMapModalOpen(false)}
        style={{ top: 10 }}
      >
        {!!selectedDayVisits.length && (
          <>
            <div style={{ marginBottom: 12 }}>
              <strong>Total Stops:</strong> {selectedDayVisits.length}
            </div>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "450px" }}
              center={{
                lat: Number(selectedDayVisits[0]?.latitude),
                lng: Number(selectedDayVisits[0]?.longitude),
              }}
              zoom={13}
            >
              <Polyline
                path={selectedDayVisits.map((visit) => ({
                  lat: Number(visit.latitude),
                  lng: Number(visit.longitude),
                }))}
              />
              {selectedDayVisits.map((visit, index) => (
                <Marker
                  key={visit.id}
                  position={{
                    lat: Number(visit.latitude),
                    lng: Number(visit.longitude),
                  }}
                  label={`${index + 1}`}
                />
              ))}
            </GoogleMap>
            <Table
              className="responsive-table"
              style={{ marginTop: 16 }}
              size="small"
              scroll={{ x: "max-content" }}
              pagination={false}
              rowKey="id"
              dataSource={selectedDayVisits}
              columns={[
                {
                  title: "#",
                  render: (_, __, index) => index + 1,
                  onCell: () => ({ "data-label": "#" }),
                },
                {
                  title: "Visit",
                  dataIndex: "visit_name",
                  onCell: () => ({ "data-label": "Visit" }),
                },
                {
                  title: "Type",
                  dataIndex: "visit_type",
                  render: (value) =>
                    value === "adhoc" ? (
                      <Tag color="orange">Adhoc</Tag>
                    ) : (
                      <Tag color="blue">Route</Tag>
                    ),
                  onCell: () => ({ "data-label": "Type" }),
                },
                {
                  title: "Time",
                  dataIndex: "checkin_time",
                  render: (v) => formatToCompanyTimezone(v),
                  onCell: () => ({ "data-label": "Time" }),
                },
              ]}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default RouteHistory;
